import React from 'react';
import { usePermissions } from '../../hooks/usePermissions';

interface PermissionGateProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null
}) => {
  const { hasPermission, hasAnyPermission, userPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions) {
    if (requireAll) {
      hasAccess = permissions.every(p => hasPermission(p));
    } else {
      hasAccess = hasAnyPermission(permissions);
    }
  } else {
    // No permission specified, allow access
    hasAccess = true;
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

interface RoleGateProps {
  children: React.ReactNode;
  roles: ('super_admin' | 'finance_admin' | 'town_admin' | 'member_admin')[];
  fallback?: React.ReactNode;
}

export const RoleGate: React.FC<RoleGateProps> = ({
  children,
  roles,
  fallback = null
}) => {
  const { isSuperAdmin, isFinanceAdmin, isTownAdmin } = usePermissions();

  const hasRole = roles.some(role => {
    switch (role) {
      case 'super_admin':
        return isSuperAdmin();
      case 'finance_admin':
        return isFinanceAdmin();
      case 'town_admin':
        return isTownAdmin();
      case 'member_admin':
        return !isSuperAdmin() && !isFinanceAdmin() && !isTownAdmin();
      default:
        return false;
    }
  });

  return hasRole ? <>{children}</> : <>{fallback}</>;
};